import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  usePtbfPreviewMutation,
  usePtbfSaveMutation,
  type PtbfRowInput,
} from '@/store/api/contractDetailPtbfApi'
import { useLazySuggestXnttNoQuery } from '@/store/api/payDataApi'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from '../purchase/dialogStyle'

// ---------------------------------------------------------------------------
// Props — exact signature for orchestrator wiring (SPEC C3).
// Guard (contract_type <> 'Outright', P-*, not forced close) is done by the
// caller before opening — the dialog does not re-check it.
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  contractNo: string
  /** Raw text of the parent screen's "Requested FX" box; parsed here, BE falls back to fixed FX. */
  requestedFx: string
  /** Parent screen's fix level — pre-fills the editable "Futures (Lastest)" field. */
  fixLevel: number | null
  onClose: () => void
  onSaved: () => void
}

/** One editable grid row — Access `public_tmp_addmany_pay_ptbf` datasheet. */
interface EditRow {
  payment_type: string
  truck_id: string
  bank_name: string
  quantity_kg: string
  pct_pay: string
  advanced_usd: string
  price: string
  futures: string
  requested_fx: string
  price_unit: string
  sub_price: string
  advanced_sub_usd: string
  request_sub_usd: string
  request_sub_vnd: string
  description: string
  vat_amount_vnd: string
}

const EMPTY_ROW: EditRow = {
  payment_type: '',
  truck_id: '',
  bank_name: '',
  quantity_kg: '',
  pct_pay: '',
  advanced_usd: '',
  price: '',
  futures: '',
  requested_fx: '',
  price_unit: '',
  sub_price: '',
  advanced_sub_usd: '',
  request_sub_usd: '',
  request_sub_vnd: '',
  description: '',
  vat_amount_vnd: '',
}

const PAYMENT_TYPE_OPTIONS = ['Provisional', 'Fixation']

/** Parse "1,234.5" → number | null. */
const parseNum = (s: string): number | null => {
  const t = s.replace(/,/g, '').trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Format numeric — thousands sep, up to `max` decimals. */
const fmtNum = (value: number | null | undefined, max = 2): string => {
  if (value === null || value === undefined) return ''
  return value.toLocaleString('en-US', { maximumFractionDigits: max })
}

/** Access Percent format: 0.7 → "70.00%". */
const fmtPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return `${(value * 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`
}

/** Row is blank when every user-typed cell is empty. */
const isRowEmpty = (r: EditRow): boolean =>
  Object.values(r).every((v) => !v.trim())

/**
 * "% Pay" UI ↔ API conversion: the user types a percent NUMBER (50 = 50%),
 * consistent with the sibling Outright dialog, but the BE compute chain
 * (Access `[% Pay]`, `PtbfInputRow.pct_pay`) expects a FRACTION (1 = 100%,
 * 0.5 = 50%) — divide by 100 before sending to /ptbf/preview and /ptbf/save.
 */
const pctToFraction = (pct: number | null): number | null =>
  pct === null ? null : pct / 100

const toInputRow = (r: EditRow): PtbfRowInput => ({
  payment_type: r.payment_type.trim() || null,
  truck_id: r.truck_id.trim() || null,
  bank_name: r.bank_name.trim() || null,
  quantity_kg: parseNum(r.quantity_kg),
  pct_pay: pctToFraction(parseNum(r.pct_pay)),
  advanced_usd: parseNum(r.advanced_usd),
  price: parseNum(r.price),
  futures: parseNum(r.futures),
  requested_fx: parseNum(r.requested_fx),
  price_unit: r.price_unit.trim() || null,
  sub_price: parseNum(r.sub_price),
  advanced_sub_usd: parseNum(r.advanced_sub_usd),
  request_sub_usd: parseNum(r.request_sub_usd),
  request_sub_vnd: parseNum(r.request_sub_vnd),
  description: r.description.trim() || null,
  vat_amount_vnd: parseNum(r.vat_amount_vnd),
})

const NUM_PREVIEW_HEADERS = [
  'To be Requested',
  'Quantity (KG)',
  'Price',
  'Futures',
  'Requested FX',
  'Advanced (USD)',
  'Request (USD)',
  'Request (VND)',
  'VAT Amount (VND)',
  'Sub Price',
  'Request sub (USD)',
  'Request sub (VND)',
  'Advanced sub (USD)',
]

const PREVIEW_HEADERS = [
  'Date',
  'Contract No',
  'Payment Type',
  'Truck ID',
  'Bank Name',
  'To be Requested',
  'Quantity (KG)',
  'Price',
  'Futures',
  'Price Unit',
  'Requested FX',
  'Advanced (USD)',
  'Request (USD)',
  'Request (VND)',
  'VAT Amount (VND)',
  'Description',
  'Sub Price',
  'Request sub (USD)',
  'Request sub (VND)',
  'Advanced sub (USD)',
]

/**
 * Contract Detail → "Add Pay PTBF" batch dialog — converts Access
 * `frmProcurementQryDetailsAddPayPTBF` ("Procurement - Pay (PTBF)").
 *
 * Top: read-only contract context (Fixed FX / Provi. on Market Price /
 * Requested FX / Applicable VAT / To be Receive / Pending.Provisional /
 * Pending.Fixation) + editable XNTT No (pre-filled via the shared suggest
 * endpoint) + editable "Futures (Lastest)" (pre-filled from the parent
 * screen's fix level). Middle: editable input grid (one row per payment
 * line, Provisional/Fixation). Bottom: "Refresh to Check" preview grid
 * mirroring the Access `ProcurementPay_WritePTBF` query + New
 * pending.Provisional / New pending.Fixation; Save inserts every previewed
 * line.
 */
export default function ContractDetailAddPayPTBFDialog({
  open,
  contractNo,
  requestedFx,
  fixLevel,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  const [runPreview, { data: preview, isLoading: isPreviewing }] = usePtbfPreviewMutation()
  const [runSave, { isLoading: isSaving }] = usePtbfSaveMutation()
  const [suggestXntt] = useLazySuggestXnttNoQuery()

  const [rows, setRows] = useState<EditRow[]>([{ ...EMPTY_ROW }])
  const [xnttNo, setXnttNo] = useState('')
  const [futures, setFutures] = useState('')

  const parsedFx = useMemo(() => parseNum(requestedFx), [requestedFx])
  const parsedFutures = useMemo(() => parseNum(futures), [futures])

  // Re-init each time the dialog opens: reset grid, prefill Futures (Lastest)
  // from the parent's fix level, fetch header context (preview with rows=[])
  // + suggest XNTT No.
  useEffect(() => {
    if (!open || !contractNo) return
    setRows([{ ...EMPTY_ROW }])
    setXnttNo('')
    setFutures(fixLevel !== null && fixLevel !== undefined ? String(fixLevel) : '')
    void runPreview({
      contract_no: contractNo,
      requested_fx: parseNum(requestedFx),
      futures: fixLevel,
      rows: [],
    })
    void (async () => {
      try {
        const sug = await suggestXntt({ contractNo }).unwrap()
        setXnttNo(sug.suggestedNo)
      } catch {
        // suggest is best-effort — user can still type the XNTT No manually
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractNo])

  const payloadRows = (): PtbfRowInput[] => rows.filter((r) => !isRowEmpty(r)).map(toInputRow)

  const handleRefresh = async () => {
    try {
      await runPreview({
        contract_no: contractNo,
        requested_fx: parsedFx,
        futures: parsedFutures,
        rows: payloadRows(),
      }).unwrap()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to refresh preview'))
    }
  }

  const handleSave = async () => {
    try {
      const res = await runSave({
        contract_no: contractNo,
        requested_fx: parsedFx,
        futures: parsedFutures,
        rows: payloadRows(),
        xntt_no: xnttNo.trim() || null,
      }).unwrap()
      snack.success(`Raw records: ${res.raw_records} — Error: ${res.errors}`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save'))
    }
  }

  const setRowField = (index: number, field: keyof EditRow, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)))
  }

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }])

  const removeRow = (index: number) =>
    setRows((prev) => (prev.length <= 1 ? [{ ...EMPTY_ROW }] : prev.filter((_, i) => i !== index)))

  const roField = (label: string, value: string, numeric = false) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={numeric ? [numSx, roSx] : roSx}
    />
  )

  const cellInput = (index: number, field: keyof EditRow, numeric = false, width = 110) => (
    <TextField
      size="small"
      value={rows[index][field]}
      onChange={(e) => setRowField(index, field, e.target.value)}
      sx={[{ minWidth: width }, ...(numeric ? [numSx] : [])]}
      disabled={isSaving}
    />
  )

  const ctx = preview?.context
  const previewRows = preview?.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="Procurement - Pay (PTBF)" sx={dialogTitleTextSx}>
          Inbound Logistics - Pay (PTBF)
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            onClick={() => void handleSave()}
            disabled={isSaving || isPreviewing}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ----- Contract context (label left / value right, 2 columns) ----- */}
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            mb: 1.5,
          }}
        >
          {/* Row 1: Contract No | XNTT No (editable) */}
          <Box>{roField('Contract No', contractNo)}</Box>
          <Box>
            <TextField
              label="XNTT No"
              size="small"
              fullWidth
              value={xnttNo}
              onChange={(e) => setXnttNo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isSaving}
            />
          </Box>

          {/* Row 2: Date | Futures (Lastest) (editable) */}
          <Box>{roField('Date', dayjs().format('DD-MMM-YYYY'))}</Box>
          <Box>
            <TextField
              label="Futures (Lastest)"
              size="small"
              fullWidth
              value={futures}
              onChange={(e) => setFutures(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isSaving}
              sx={numSx}
            />
          </Box>

          {/* Row 3: Fixed FX (on Contract) | Provi. on Market Price */}
          <Box>{roField('Fixed FX (on Contract)', fmtNum(ctx?.fixed_fx), true)}</Box>
          <Box>{roField('Provi. on Market Price', fmtPercent(ctx?.provi_market_rate), true)}</Box>

          {/* Row 4: Requested FX (resolved by BE) | Applicable VAT */}
          <Box>{roField('Requested FX', fmtNum(ctx?.requested_fx), true)}</Box>
          <Box>{roField('Applicable VAT', ctx?.vat_txt ?? '')}</Box>

          {/* Row 5: To be Receive | — */}
          <Box>{roField('To be Receive', fmtNum(ctx?.pending), true)}</Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />

          {/* Row 6: Pending.Provisional | Pending.Fixation */}
          <Box>{roField('Pending.Provisional', fmtNum(ctx?.pending_provisional), true)}</Box>
          <Box>{roField('Pending.Fixation', fmtNum(ctx?.pending_fixation), true)}</Box>
        </Box>

        <Typography sx={{ color: 'error.main', fontStyle: 'italic', fontSize: '0.75rem', mb: 1 }}>
          {'* Auto fill Advanced (Contract + TruckID + Quantity) If you input Null Value in "Advanced USD" column'}
        </Typography>

        {/* ----- Input grid (Access subForm: tmp_addmany_pay_ptbf) ----- */}
        <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5 }}>
          Input data in the below form
        </Typography>
        <Box sx={{ overflowX: 'auto', mb: 1 }}>
          <Table size="small" sx={{ '& td, & th': { px: 0.5, py: 0.5, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Payment Type</TableCell>
                <TableCell>Truck ID</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell align="right">Quantity KG</TableCell>
                <TableCell align="right">% Pay</TableCell>
                <TableCell align="right">Advanced USD</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Futures</TableCell>
                <TableCell align="right">Requested FX</TableCell>
                <TableCell>Price Unit</TableCell>
                <TableCell align="right">Sub Price</TableCell>
                <TableCell align="right">Advanced sub USD</TableCell>
                <TableCell align="right">Request sub USD</TableCell>
                <TableCell align="right">Request sub VND</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">VAT Amount VND</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Autocomplete
                      options={PAYMENT_TYPE_OPTIONS}
                      value={row.payment_type || null}
                      onChange={(_e, v) => setRowField(i, 'payment_type', v ?? '')}
                      disabled={isSaving}
                      size="small"
                      sx={{ minWidth: 140 }}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  </TableCell>
                  <TableCell>{cellInput(i, 'truck_id', false, 90)}</TableCell>
                  <TableCell>{cellInput(i, 'bank_name', false, 120)}</TableCell>
                  <TableCell>{cellInput(i, 'quantity_kg', true, 100)}</TableCell>
                  <TableCell>{cellInput(i, 'pct_pay', true, 70)}</TableCell>
                  <TableCell>{cellInput(i, 'advanced_usd', true, 110)}</TableCell>
                  <TableCell>{cellInput(i, 'price', true, 90)}</TableCell>
                  <TableCell>{cellInput(i, 'futures', true, 90)}</TableCell>
                  <TableCell>{cellInput(i, 'requested_fx', true, 100)}</TableCell>
                  <TableCell>{cellInput(i, 'price_unit', false, 90)}</TableCell>
                  <TableCell>{cellInput(i, 'sub_price', true, 90)}</TableCell>
                  <TableCell>{cellInput(i, 'advanced_sub_usd', true, 110)}</TableCell>
                  <TableCell>{cellInput(i, 'request_sub_usd', true, 110)}</TableCell>
                  <TableCell>{cellInput(i, 'request_sub_vnd', true, 110)}</TableCell>
                  <TableCell>{cellInput(i, 'description', false, 160)}</TableCell>
                  <TableCell>{cellInput(i, 'vat_amount_vnd', true, 110)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => removeRow(i)}
                      disabled={isSaving}
                      aria-label="Remove row"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addRow}
          disabled={isSaving}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Add row
        </Button>

        {/* ----- Check section: New pendings + Refresh + preview grid ----- */}
        <Divider sx={{ mb: 1.5 }} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 1 }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', flex: '1 1 auto' }}>
            Check data in the below form before click &quot;SAVE&quot;
          </Typography>
          <Box sx={{ width: { xs: '100%', sm: 200 } }}>
            {roField('New pending.Provisional', fmtNum(preview?.new_pending_provisional), true)}
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 200 } }}>
            {roField('New pending.Fixation', fmtNum(preview?.new_pending_fixation), true)}
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleRefresh()}
            disabled={isPreviewing || isSaving}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {isPreviewing ? <CircularProgress size={14} color="inherit" /> : 'Refresh to Check'}
          </Button>
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { px: 0.5, py: 0.5, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {PREVIEW_HEADERS.map((h) => (
                  <TableCell key={h} align={NUM_PREVIEW_HEADERS.includes(h) ? 'right' : 'left'}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={PREVIEW_HEADERS.length}>
                    <Typography variant="caption" color="text.secondary">
                      {isPreviewing
                        ? 'Loading…'
                        : 'No preview rows — input data above then click "Refresh to Check".'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                previewRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.date ? dayjs(r.date).format('DD-MMM-YYYY') : ''}</TableCell>
                    <TableCell>{r.contract_no}</TableCell>
                    <TableCell>{r.payment_type ?? ''}</TableCell>
                    <TableCell>{r.truck_id ?? ''}</TableCell>
                    <TableCell>{r.bank_name ?? ''}</TableCell>
                    <TableCell align="right">{fmtNum(r.to_be_requested)}</TableCell>
                    <TableCell align="right">{fmtNum(r.quantity_kg)}</TableCell>
                    <TableCell align="right">{fmtNum(r.price)}</TableCell>
                    <TableCell align="right">{fmtNum(r.futures)}</TableCell>
                    <TableCell>{r.price_unit ?? ''}</TableCell>
                    <TableCell align="right">{fmtNum(r.requested_fx)}</TableCell>
                    <TableCell align="right">{fmtNum(r.advanced_usd, 3)}</TableCell>
                    <TableCell align="right">{fmtNum(r.request_usd, 3)}</TableCell>
                    <TableCell align="right">{fmtNum(r.request_vnd, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(r.vat_amount_vnd, 0)}</TableCell>
                    <TableCell>{r.description ?? ''}</TableCell>
                    <TableCell align="right">{fmtNum(r.sub_price)}</TableCell>
                    <TableCell align="right">{fmtNum(r.request_sub_usd, 3)}</TableCell>
                    <TableCell align="right">{fmtNum(r.request_sub_vnd)}</TableCell>
                    <TableCell align="right">{fmtNum(r.advanced_sub_usd, 3)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
